import { AxiosAdapter, AxiosRequestConfig, AxiosResponse } from "axios";

const ABSOLUTE_URL = /^[a-z][a-z\d+\-.]*:\/\//i;

const joinUrl = (baseURL: string | undefined, url: string | undefined): string => {
  if (!url) return baseURL ?? "";
  if (!baseURL || ABSOLUTE_URL.test(url)) return url;
  return `${baseURL.replace(/\/+$/, "")}/${url.replace(/^\/+/, "")}`;
};

/** axios has already merged and flattened the headers; keep the plain values only. */
const toFetchHeaders = (headers: AxiosRequestConfig["headers"]): Record<string, string> => {
  const result: Record<string, string> = {};
  Object.entries(headers ?? {}).forEach(([name, value]) => {
    if (typeof value === "string" || typeof value === "number") {
      result[name] = String(value);
    }
  });
  return result;
};

/**
 * An axios adapter that sends the request with fetch({ keepalive: true }) instead of XHR, so a
 * request made while the page is being hidden still leaves after the browser freezes the page
 * (iOS does that as soon as the phone locks). Pass it per request:
 * axiosInstance.post(url, body, { adapter: keepaliveAdapter }). The interceptors (token, studio
 * header) and the JSON transforms run as for any other call.
 */
export const keepaliveAdapter: AxiosAdapter = async (config) => {
  const send = (keepalive: boolean) =>
    fetch(joinUrl(config.baseURL, config.url), {
      method: (config.method ?? "get").toUpperCase(),
      headers: toFetchHeaders(config.headers),
      body: config.data,
      keepalive,
    });

  let response: Response;
  try {
    response = await send(true);
  } catch (error) {
    // Older browsers refuse keepalive on a cross-origin request that needs a preflight
    // (TypeError); a plain request still has a good chance to leave before the page freezes.
    if (!(error instanceof TypeError)) throw error;
    response = await send(false);
  }

  const axiosResponse: AxiosResponse = {
    data: await response.text(),
    status: response.status,
    statusText: response.statusText,
    headers: {},
    config,
    request: null,
  };

  const validateStatus = config.validateStatus ?? ((status: number) => status >= 200 && status < 300);
  if (validateStatus(response.status)) {
    return axiosResponse;
  }

  throw Object.assign(new Error(`Request failed with status code ${response.status}`), {
    config,
    response: axiosResponse,
    isAxiosError: true,
  });
};
